import { getUserAuthorization } from '@sketch/modules-common'
import {
  GetUserCredentialsDocument,
  GetUserCredentialsQuery,
} from '@sketch/gql-types'
import { log, uniqueId } from '@sketch/utils'
import { ApolloClient } from '@apollo/client'
import { WebsocketContext } from '@sketch/common-types'
import config from '@sketch/env-config'

export const createPresencesChannels = (
  apolloClient: ApolloClient<any>,
  { createAuthenticatedSocket, createSocketChannels }: WebsocketContext
) => {
  const cache = apolloClient.cache

  const socketUri = config.presences_ws_uri

  const socket = createAuthenticatedSocket(socketUri, () => apolloClient)

  const channels = createSocketChannels(socket)

  const authorization = getUserAuthorization(cache)
  let currentAuthToken = authorization?.authToken || ''

  // Apollo v3 migration: Replace cache.watch with watchQuery
  const observable = apolloClient.watchQuery({
    query: GetUserCredentialsDocument,
    variables: { key: uniqueId() }, // Keep unique variables for separate watchers
    fetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: false,
  })

  observable.subscribe({
    next: async ({ data }) => {
      const userCredentials = data as GetUserCredentialsQuery
      const newAuthToken = userCredentials.userCredentials?.authToken

      if (currentAuthToken === newAuthToken) {
        log('Socket -> Credentials changed but token did not')
        return
      }

      if (!newAuthToken) {
        log('Socket -> Credentials changed but token isEmpty')
        socket.disconnect()
        return
      }

      currentAuthToken = newAuthToken

      /**
       * Calling `conn.close` is the recommended method to reconnect
       * the websocket with new authentication parameters
       *
       * `conn` property is only available when WebSocket connection is
       *  established, so we need to check if it is available before executing
       *
       * Seems like `conn` is not defined in TypeScript types as it is
       * an internal property, that's why we cast it to any before checking
       * the availability
       *
       * https://hexdocs.pm/absinthe/apollo.html#reconnecting-the-websocket-link
       */
      const connectedSocket = socket as any
      if (connectedSocket.conn) {
        log('Socket -> Refreshing socket connection with new credentials')
        connectedSocket.conn.close()
      } else {
        log('Socket -> Open socket because there was no connection')
        await socket.connect()
      }
    },
    error: error => {
      log('Socket -> Error watching user credentials:', error)
    },
  })

  return channels
}
